import { HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { SideBarService } from '@services/side-bar/side-bar.service';
import { StorageService } from '@services/storage/storage.service';
import { LanguageDetail } from '@services/translation/language-detail';
import { LANGUAGES } from '@services/translation/languages-definitions';
import { UtilsService } from '@services/utils/utils.service';

import { MenuNotificationService } from '@services/menu-notification/menu-notification.service';
import { TranslationService } from '@services/translation/translation.service';

import { LegalNoticeModalComponent } from '@components/header/legal-notice/legal-notice-modal.component';
import { SupportContactComponent } from '@components/support/support-contact/support-contact.component';
import { SupportDocumentationComponent } from '@components/support/support-documentation/support-documentation.component';
import { Organization } from '@models/organization/organization';
import { MenuNotification, OperationType } from '@models/sidebar/menu-notification';
import { Role } from '@models/user/role';
import { User } from '@models/user/user';
import { BrandingService } from '@services/administration/organization/configuration/branding/branding.service';
import { OrganizationService } from '@services/administration/organization/organization.service';
import { FirebaseAuthenticationService } from '@services/firebase/auth/firebase-authentication.service';
import { PortalService } from '@services/header/portal.service';
import { SelectPageService } from '@services/left-menu/select-page/select-page.service';
import { ModalService } from '@services/modal/modal.service';

@Component({
    selector: 'scitis-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})

export class HeaderComponent implements OnInit {

  @Output()
  sidebarStatusKey: EventEmitter<string> = new EventEmitter<string>();

  public userMail: string = '';
  public userDisplayName: string = '';
  public userRole: string = '';
  public activeLanguageFlag: string = LANGUAGES[0].flag;
  public LANGUAGES: LanguageDetail[] = LANGUAGES;
  public isSidebarOpen = false;
  public portalVersion: string = '';
  public organizationId: number = 0;
  public notificationRetention: number = 0;
  public isVisible: boolean = true;
  public isSuperAdminRole = false;
  public isDistributorRole = false;
  public supportTicketUrl: string | null = null;
  public userManualUrl: string | null = null;

  public isViewerRole = false;
  private isAdminRole = false;
  private isTechAdminRole = false;
  private user: User = new User();
  private SIDEBAR_STATUS_KEY = 'SIDEBAR_STATUS_';
  private privacyUrl: string = '';
  private userAgreementUrl: string = '';
  private SELECTED_PAGE_KEY = '';

  constructor(private activatedRoute: ActivatedRoute,
              private sideBarService: SideBarService,
              private loadingIndicator: LoadingIndicatorService,
              private translationService: TranslationService,
              private utilsService: UtilsService,
              private storageService: StorageService,
              private router: Router,
              private menuNotificationService: MenuNotificationService,
              private portalService: PortalService,
              private modalService: ModalService,
              private organizationService: OrganizationService,
              private selectedPageService: SelectPageService,
              private brandingService: BrandingService,
              private firebaseAuthService: FirebaseAuthenticationService) {

  }

  public get hasInvalidPrivacyURL(): boolean {
    return !this.privacyUrl || this.privacyUrl.isNullOrWhitespace();
  }

  public get hasInvalidUserAgreementURL(): boolean {
    return !this.userAgreementUrl || this.userAgreementUrl.isNullOrWhitespace();
  }

  get isAdminOrHigher(): boolean {
    return this.utilsService.isAdminOrHigher(this.isAdminRole, this.isTechAdminRole, this.isSuperAdminRole);
  }

  get statusClass(): string {
    if (this.sideBarService.isSidebarOpen) {
      return 'primary-menu-toggle-close';
    }
    return '';
  }

  @HostListener('document:visibilitychange')
  visibilitychange() {
    if (document.hidden) {
      this.isVisible = false;
    } else {
      this.isVisible = true;
    }
  }

  changeLanguage(language: string): void {
    console.log('Switch to: ' + language);
    this.translationService.useLanguage(language);
    this.activeLanguageFlag = this.translationService.getActiveLanguageFlag();
    this.firebaseAuthService.saveUserLanguage(this.user, language);
  }

  toggleSidebar() {
    this.sideBarService.toggleSidebar(this.SIDEBAR_STATUS_KEY);
    this.setSidebarStatus();
  }

  ngOnInit(): void {
    this.setPortalVersion();
    this.getUserManualUrl();
    this.loadUser();
    this.getOrganizationDetails();
  }

  onLogout() {
    this.loadingIndicator.show();
    this.sideBarService.hideSidebar();
    this.brandingService.removeOrganizationBranding();
    this.firebaseAuthService.signOutUser();
  }

  moveToSite(target: string) {
    this.storageService.writeValueToIndexedDb(this.SELECTED_PAGE_KEY, '');
    this.selectedPageService.setSelectedPageId(null);
    this.router.navigate([target]);
  }

  goToDashboard(event: any): void {
    event.preventDefault();
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_USER_MACHINERY_GROUP, this.user.firebaseUid, '', ''));
    this.router.navigate(['dashboard']);
  }

  getPortalVersion() {
    return this.portalVersion;
  }

  openSupportContact() {
    const modalRef = this.modalService.open(SupportContactComponent, {size: 'xl'});
    modalRef.componentInstance.organizationId = this.organizationId;
  }

  openSupportDocumentation() {
    this.modalService.open(SupportDocumentationComponent, {size: 'lg'});
  }

  openPolicyUrl() {
    if (!this.privacyUrl) {
      return;
    }
    window.open(this.privacyUrl, '_blank');
  }

  openUserAgreementUrl() {
    if (!this.userAgreementUrl) {
      return;
    }
    window.open(this.userAgreementUrl, '_blank');
  }

  openLegalNotice() {
    this.modalService.open(LegalNoticeModalComponent, {size: 'lg'});
  }

  setSidebarStatus(): void {
    this.storageService.readValueFromIndexedDb<string>(this.SIDEBAR_STATUS_KEY).then(
        (value) => {
          if (value !== null && undefined !== value) {
            this.isSidebarOpen = JSON.parse(value);
            if (this.isSidebarOpen !== this.sideBarService.isSidebarOpen) {
              this.sideBarService.toggleSidebar(this.SIDEBAR_STATUS_KEY);
            }
          } else {
            this.isSidebarOpen = this.sideBarService.isSidebarOpen;
          }
        }, _onrejected => {
          this.isSidebarOpen = this.sideBarService.isSidebarOpen;
          console.log('Error', _onrejected);
        });
  }

  private setPortalVersion() {
    this.portalService.getPortalVersion().subscribe(
        {
          next: (portalVersion) => {
            this.portalVersion = portalVersion[0];
          }, error: (error) => {
            console.log(error);
          }
        });
  }

  private loadUser(): void {
    this.loadingIndicator.show();
    this.user = this.activatedRoute.snapshot.data.userDetails;
    this.firebaseAuthService.getLoggedUserDetails().subscribe(
        {
          next: (result: User) => {
            this.user = result;
            const userRole = new Role(result);
            this.isSuperAdminRole = userRole.isSuperAdminRole;
            if (this.isSuperAdminRole) {
              this.getSupportTicketUrl();
            }
            this.isAdminRole = userRole.isAdminRole;
            this.isDistributorRole = userRole.isDistributorRole;
            this.isTechAdminRole = userRole.isTechAdminRole;
            this.isViewerRole = userRole.isViewerRole;
            this.userDisplayName = result.displayName;
            this.userMail = result.username;
            this.userRole = result.role;
            this.organizationId = result.parentId;
            this.notificationRetention = result.notificationRetentionDays;
            this.translationService.useLanguage(result.language);
            this.activeLanguageFlag = this.translationService.getActiveLanguageFlag();
            this.SIDEBAR_STATUS_KEY = 'SIDEBAR_STATUS_' + this.user.firebaseUid;
            this.sidebarStatusKey.emit(this.SIDEBAR_STATUS_KEY);
            this.SELECTED_PAGE_KEY = 'selectedPage' + this.user.firebaseUid;
            this.setSidebarStatus();
            this.loadingIndicator.hide();
          }, error: (_error: HttpResponse<any>) => {
            this.firebaseAuthService.signOutUser();
          }
        });
  }

  private getOrganizationDetails() {
    this.organizationService.getOrganizationDetails(this.organizationId).subscribe(
        {
          next: (organization: Organization) => {
            this.privacyUrl = organization.privacyPolicyUrl;
            this.userAgreementUrl = organization.userAgreementUrl;
          }, error: (error) => {
            console.log('Error getting organization details', error);
          }
        });
  }

  private getSupportTicketUrl() {
    this.portalService.getSupportTicketUrl().subscribe(
        {
          next: (url: string[]) => {
            if (url && url.length > 0 && url[0])
              this.supportTicketUrl = url[0];
          }, error: (error) => {
            console.log(error);
          }
        });
  }


  openSupportTicket() {
    if(!this.supportTicketUrl) {
      return;
    }
    window.open(this.supportTicketUrl, '_blank');
  }

  private getUserManualUrl() {
    this.portalService.getUserManualUrl().subscribe(
      {
        next: (url: string[]) => {
          if (url && url.length > 0 && url[0])
            this.userManualUrl = url[0];
        }, error: (error) => {
          console.log(error);
        }
      }
    );
  }

  openUserManual() {
    if (!this.userManualUrl) {
      return;
    }
    window.open(this.userManualUrl, '_blank');
  }
}
